// iter138 — Habana Updates list + detail (bulletin / article system).
//
// The article body is rendered AUTOMATICALLY on page load. When a published
// update has empty `full_content` we fall back to `short_description` so
// the reader never lands on a blank screen — the previous code silently
// hid the entire body block when full_content was empty.
//
// Caching: every list fetch warms a small offline cache (key
// `habano.updates.cache`) so the detail page can still render when the
// device is offline or the user landed directly on `/updates/:id`.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { UPDATE_BADGE } from "../components/HavanaUpdates";
import { useI18n } from "../lib/i18n";
import { lsGet, lsSet } from "../lib/safeStorage";

const CACHE_KEY = "habano.updates.cache";

function readCache() {
  try { const raw = lsGet(CACHE_KEY); return raw ? JSON.parse(raw) : []; } catch { return []; }
}
function writeCache(rows) {
  try { lsSet(CACHE_KEY, JSON.stringify(rows || [])); } catch { /* ignore */ }
}

/** Bilingual picker with EN/ES fallback so the reader never sees "" because
 *  the admin only filled one language. */
function pickLang(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  return field[lang] || field.en || field.es || "";
}

// iter148 — Human-readable date. "2026-06-01" → "June 1, 2026" (EN) or
// "1 de junio de 2026" (ES). Returns the raw string on parse failure so
// we never break the meta row.
function formatLongDate(raw, lang) {
  if (!raw) return "";
  const iso = String(raw).length >= 10 ? String(raw).slice(0, 10) : String(raw);
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return raw;
  try {
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
    });
  } catch { return raw; }
}

export function UpdatesList() {
  const [rows, setRows] = useState(() => readCache());
  const [loading, setLoading] = useState(rows.length === 0);
  const { lang } = useI18n();

  useEffect(() => {
    api.get("/updates", { params: { limit: 20 } })
      .then(({ data }) => {
        setRows(data || []);
        writeCache(data || []);
      })
      .catch(() => { /* keep cache */ })
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="updates-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 flex items-center gap-3">
        <Link to="/" className="btn-ghost p-2" aria-label="Back" data-testid="updates-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </Link>
        <div>
          <div className="eyebrow text-gold">Bulletin</div>
          <h1 className="font-display text-4xl mt-1 text-foreground leading-tight">Havana Updates</h1>
        </div>
      </header>

      <section className="px-5 mt-6 space-y-4">
        {loading && rows.length === 0 && (
          <div className="text-foreground/55 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="lux-card p-8 text-center text-foreground/55 text-sm" data-testid="updates-empty">
            No active updates right now. Check back soon.
          </div>
        )}
        {rows.map((u) => {
          const cat = UPDATE_BADGE[u.category] || UPDATE_BADGE.Events;
          return (
            <Link
              key={u.id}
              to={`/updates/${u.id}`}
              data-testid={`updates-row-${u.id}`}
              className="lux-card p-5 block hover:border-gold-strong transition-colors"
            >
              <div className="flex items-start gap-3">
                <span
                  className="px-2 py-1 text-[10px] uppercase tracking-widest font-medium shrink-0"
                  style={{ background: cat.bg, color: cat.color }}
                >
                  {u.category}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-xl text-foreground leading-snug">{pickLang(u.headline, lang)}</h2>
                  <p className="mt-2 text-[15px] text-foreground/70 leading-relaxed">{pickLang(u.short_description, lang)}</p>
                  <div className="text-[14px] text-foreground/45 mt-3">Expires {u.expiry_date}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </section>
      <BottomNav />
    </main>
  );
}

export function UpdateDetail() {
  const { id } = useParams();
  const { lang } = useI18n();
  const cache = useMemo(() => readCache(), []);
  // Cache-first render so the page never starts blank.
  const [u, setU] = useState(() => cache.find((x) => x.id === id) || null);
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/updates/${id}`)
      .then(({ data }) => { setU(data); setLoaded(true); })
      .catch((e) => {
        // Network failed but we may already have it cached — only flag
        // an error when there's literally nothing to show.
        if (!u) setErr(e?.response?.data?.detail || "Not found");
        setLoaded(true);
      });
  }, [id]);

  if (err && !u) {
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background" data-testid="update-not-found">
        <div className="text-center">
          <div className="eyebrow">Bulletin</div>
          <h1 className="font-display text-3xl mt-2 text-foreground">Update not available</h1>
          <p className="mt-3 text-foreground/55" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>{err}</p>
          <Link to="/updates" className="btn-outline mt-6 inline-flex" data-testid="update-not-found-back">Back to updates</Link>
        </div>
      </main>
    );
  }
  if (!u && !loaded) {
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background" data-testid="update-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40" />
      </main>
    );
  }
  if (!u) return null;

  const cat = UPDATE_BADGE[u.category] || UPDATE_BADGE.Events;
  const isWarn = ["Urgent", "Customs", "Currency", "Safety"].includes(u.category);
  const headline = pickLang(u.headline, lang);
  const sub = pickLang(u.short_description, lang);
  const fullRaw = pickLang(u.full_content, lang);

  // iter138 — Body renders AUTOMATICALLY. If full_content is empty in both
  // languages, fall back to short_description so the reader never sees a
  // hollow page (only the badge + headline like the previous bug).
  const bodyText = fullRaw && fullRaw.trim().length > 0 ? fullRaw : sub;
  const paragraphs = (bodyText || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <main data-testid="update-detail" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 flex items-center gap-3">
        <Link to="/updates" className="btn-ghost p-2" aria-label="Back" data-testid="update-detail-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </header>
      <section className="px-5 mt-2 max-w-2xl">
        <span
          data-testid="update-detail-category"
          className="inline-block px-2 py-1 text-[10px] uppercase tracking-widest font-medium"
          style={{ background: cat.bg, color: cat.color }}
        >
          {u.category}
        </span>
        <h1
          data-testid="update-detail-headline"
          className="font-display text-foreground mt-3 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 30, lineHeight: 1.15 }}
        >
          {headline}
        </h1>
        {sub && bodyText !== sub && (
          <p
            data-testid="update-detail-sub"
            className="mt-4 text-foreground/80 leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}
          >
            {sub}
          </p>
        )}
        {isWarn && (
          <div
            data-testid="update-detail-readcarefully"
            className="mt-5 inline-flex items-center gap-2 px-3 py-2 border"
            style={{ borderColor: "rgba(245, 158, 11, 0.55)", background: "rgba(245, 158, 11, 0.06)" }}
          >
            <AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
            <span className="uppercase tracking-widest text-amber-300"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}>
              Read carefully
            </span>
          </div>
        )}

        {/* Article body — ALWAYS rendered. Never hidden behind a `&&`. */}
        <article
          data-testid="update-detail-body"
          className="mt-6 space-y-4"
          style={{ padding: "0 16px" }}
        >
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => (
              <p
                key={i}
                data-testid={`update-detail-paragraph-${i}`}
                className="whitespace-pre-wrap"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 16,
                  lineHeight: 1.8,
                  color: "#F5F1E8",
                  margin: 0,
                }}
              >
                {p}
              </p>
            ))
          ) : (
            <p
              data-testid="update-detail-empty"
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#B0AAA3", lineHeight: 1.6 }}
            >
              {lang === "es" ? "Contenido próximamente" : "Content coming soon"}
            </p>
          )}
        </article>

        <div className="text-foreground/45 mt-8"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}
          data-testid="update-detail-meta"
        >
          {lang === "es"
            ? `Publicado el ${formatLongDate(u.created_at, "es")} · Vence el ${formatLongDate(u.expiry_date, "es")}`
            : `Posted ${formatLongDate(u.created_at, "en")} · Expires ${formatLongDate(u.expiry_date, "en")}`}
        </div>
      </section>
      <BottomNav />
    </main>
  );
}
